/**
 * OrganizationModal Component
 *
 * Unified modal for adding and editing organization-specific cross-tenant access settings.
 * Uses the same layout as Default Settings card with Inbound/Outbound access tabs.
 *
 * In add mode, includes a tenant ID input field.
 * In organization mode, each setting has "Inherit from default" option (null = inherited).
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Field,
  Input,
  tokens,
} from '@fluentui/react-components';
import { AsyncButton } from '../../common/AsyncButton';
import { CrossTenantSettingsPanel } from './CrossTenantSettingsPanel';
import type {
  CrossTenantPartner,
  InboundTrust,
  B2BAccessType,
} from '../../../types/crossTenantAccess';

/**
 * Settings returned by the modal on save.
 */
export interface OrganizationModalSettings {
  tenantId?: string;
  inboundTrust: InboundTrust | null;
  b2bCollaboration: { inbound: B2BAccessType | null; outbound: B2BAccessType | null };
}

interface OrganizationModalProps {
  /**
   * Whether the modal is open.
   */
  open: boolean;
  /**
   * The partner organization to edit, or null for add mode.
   */
  partner: CrossTenantPartner | null;
  /**
   * Callback when modal is closed (cancel or backdrop click).
   */
  onClose: () => void;
  /**
   * Callback when save is clicked with settings.
   * In add mode, tenantId is included.
   * null values for inboundTrust/b2bCollaboration indicate inherited from default.
   */
  onSave: (settings: OrganizationModalSettings) => void;
  /**
   * Disable save button during API calls.
   */
  saving?: boolean;
}

export function OrganizationModal({
  open,
  partner,
  onClose,
  onSave,
  saving = false,
}: OrganizationModalProps) {
  const isAddMode = partner === null;

  // Tenant ID for add mode
  const [tenantId, setTenantId] = useState('');

  // B2B collaboration state (null = inherited from default)
  const [b2bCollaboration, setB2bCollaboration] = useState<{
    inbound: B2BAccessType | null;
    outbound: B2BAccessType | null;
  }>({ inbound: null, outbound: null });

  // Trust settings state (null = inherited from default)
  const [inboundTrust, setInboundTrust] = useState<InboundTrust | null>(null);

  // Reset state when partner changes or modal opens
  useEffect(() => {
    if (open) {
      if (partner) {
        // Edit mode - load partner settings
        setTenantId('');
        setB2bCollaboration(partner.b2bCollaboration);
        setInboundTrust(partner.inboundTrust);
      } else {
        // Add mode - reset to defaults (all inherited)
        setTenantId('');
        setB2bCollaboration({ inbound: null, outbound: null });
        setInboundTrust(null);
      }
    }
  }, [open, partner]);

  const handleSettingsChange = (settings: {
    b2bCollaboration: { inbound: B2BAccessType | null; outbound: B2BAccessType | null };
    inboundTrust: InboundTrust | null;
  }) => {
    setB2bCollaboration(settings.b2bCollaboration);
    setInboundTrust(settings.inboundTrust);
  };

  const handleSave = () => {
    const settings: OrganizationModalSettings = {
      inboundTrust,
      b2bCollaboration,
    };
    if (isAddMode) {
      settings.tenantId = tenantId;
    }
    onSave(settings);
  };

  const canSave = isAddMode ? tenantId.trim().length > 0 : true;
  const title = partner ? `Edit Organization - ${partner.displayName}` : 'Add Organization';
  const saveLabel = partner ? 'Save Changes' : 'Add Organization';

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface style={{ maxWidth: '600px' }}>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent style={{ height: '580px', overflow: 'auto' }}>
            {isAddMode && (
              <Field
                label="Domain Name or Tenant ID"
                hint="Enter the Entra ID domain name or tenant ID of the organization"
                required
                style={{ marginBottom: tokens.spacingVerticalL }}
              >
                <Input
                  value={tenantId}
                  onChange={(_, data) => setTenantId(data.value)}
                  placeholder="e.g., contoso.onmicrosoft.com or tenant GUID"
                  disabled={saving}
                />
              </Field>
            )}

            <CrossTenantSettingsPanel
              mode="organization"
              b2bCollaboration={b2bCollaboration}
              inboundTrust={inboundTrust}
              onChange={handleSettingsChange}
              disabled={saving}
            />
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <AsyncButton
              appearance="primary"
              onClick={handleSave}
              disabled={saving || !canSave}
              loading={saving}
              loadingText="Saving..."
            >
              {saveLabel}
            </AsyncButton>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/**
 * @deprecated Use OrganizationModal instead
 */
export const EditOrganizationModal = OrganizationModal;
